import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { baseApiURL } from '../../baseUrl';
import { FiUser, FiSearch, FiDownload, FiFilter, FiRefreshCw, FiExternalLink, FiFileText, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { sortEnrollmentNo } from '../../utils/enrollmentSorter';

// Branch normalization helper
const normalizeBranch = (branchStr = '') => {
    const b = branchStr.trim().toLowerCase();
    if (b === 'cse' || b.includes('computer science') || b.includes('computer science & engineering') || b.includes('computer science and engineering')) {
        return 'CSE';
    }
    if (b === 'ece' || b.includes('electronics') || b.includes('communication')) {
        return 'ECE';
    }
    if (b === 'eee' || b.includes('electrical')) {
        return 'EEE';
    }
    if (b === 'it' || b.includes('information technology')) {
        return 'IT';
    }
    if (b === 'csm' || b.includes('ai') || b.includes('machine learning')) {
        return 'CSM';
    }
    if (b === 'csd' || b.includes('data science')) {
        return 'CSD';
    }
    if (b === 'csc' || b.includes('cyber')) {
        return 'CSC';
    }
    if (b === 'mech' || b.includes('mechanical')) {
        return 'MECH';
    }
    if (b === 'civil' || b.includes('civil')) {
        return 'CIVIL';
    }
    return branchStr.trim().toUpperCase();
};

const StudentProfiles = () => {
    const [profiles, setProfiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortAscending, setSortAscending] = useState(true);

    const [filters, setFilters] = useState({
        branch: '',
        semester: '',
        batch: '',
        minCGPA: '',
        maxBacklogs: '',
    });

    useEffect(() => {
        fetchProfiles();
    }, []);

    const fetchProfiles = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${baseApiURL()}/placement/studentprofiles/get`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            if (res.data.success) {
                const sorted = [...(res.data.profiles || [])].sort(sortEnrollmentNo);
                setProfiles(sorted);
            }
        } catch (err) {
            console.error('Error fetching profiles', err);
            toast.error('Failed to fetch student profiles');
        } finally {
            setLoading(false);
        }
    };

    // Dynamically derive unique filter options from the loaded profiles
    const filterOptions = useMemo(() => {
        const branchMap = new Map();
        const batches = new Set();
        const semesters = new Set();

        profiles.forEach((p) => {
            if (p.branch) {
                const norm = normalizeBranch(p.branch);
                if (!branchMap.has(norm)) {
                    branchMap.set(norm, p.branch);
                }
            }
            if (p.batch) batches.add(String(p.batch));
            if (p.semester) semesters.add(String(p.semester));
        });

        return {
            branches: Array.from(branchMap.keys()).sort(),
            batches: Array.from(batches).sort((a, b) => Number(b) - Number(a)),
            semesters: Array.from(semesters).sort((a, b) => Number(a) - Number(b)),
        };
    }, [profiles]);

    // Live reactive filtering & strict JNTU roll ascending sort
    const filtered = useMemo(() => {
        let result = profiles.filter((p) => {
            // Branch match (intelligent normalization)
            if (filters.branch) {
                const studentNormBranch = normalizeBranch(p.branch || '');
                const filterNormBranch = normalizeBranch(filters.branch);
                if (studentNormBranch !== filterNormBranch) {
                    return false;
                }
            }
            // Semester match
            if (filters.semester) {
                if (String(p.semester).trim() !== String(filters.semester).trim()) {
                    return false;
                }
            }
            // Batch match
            if (filters.batch) {
                if (String(p.batch).trim() !== String(filters.batch).trim()) {
                    return false;
                }
            }
            // Min CGPA match
            if (filters.minCGPA !== '' && filters.minCGPA !== null) {
                const min = parseFloat(filters.minCGPA);
                if (!isNaN(min) && (p.cgpa === undefined || p.cgpa === null || p.cgpa < min)) {
                    return false;
                }
            }
            // Max Backlogs match
            if (filters.maxBacklogs !== '' && filters.maxBacklogs !== null) {
                const max = parseInt(filters.maxBacklogs, 10);
                if (!isNaN(max) && (p.activeBacklogs || 0) > max) {
                    return false;
                }
            }
            // Search query match
            if (searchQuery.trim()) {
                const q = searchQuery.toLowerCase().trim();
                const roll = (p.enrollmentNo || '').toLowerCase();
                const name = (p.fullName || '').toLowerCase();
                const email = (p.email || '').toLowerCase();
                const branch = (p.branch || '').toLowerCase();
                if (!roll.includes(q) && !name.includes(q) && !email.includes(q) && !branch.includes(q)) {
                    return false;
                }
            }
            return true;
        });

        const sorted = result.sort(sortEnrollmentNo);
        return sortAscending ? sorted : [...sorted].reverse();
    }, [profiles, filters, searchQuery, sortAscending]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleResetFilter = () => {
        setFilters({ branch: '', semester: '', batch: '', minCGPA: '', maxBacklogs: '' });
        setSearchQuery('');
    };

    const handleExportCSV = () => {
        if (filtered.length === 0) {
            toast.error('No data to export');
            return;
        }
        const headers = ['Enrollment No', 'Name', 'Email', 'Phone', 'Branch', 'Semester', 'Batch', 'Gender', 'CGPA', '10th%', '12th%', 'Active Backlogs', 'Resume Link', 'LinkedIn'];
        const rows = filtered.map(p => [
            p.enrollmentNo,
            p.fullName,
            p.email,
            p.phoneNumber,
            p.branch,
            p.semester,
            p.batch,
            p.gender,
            p.cgpa ?? '',
            p.tenthPercentage ?? '',
            p.twelfthPercentage ?? '',
            p.activeBacklogs ?? 0,
            p.resumeLink || '',
            p.linkedinLink || ''
        ]);
        const csvContent = [headers, ...rows]
            .map(row => row.map(cell => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(','))
            .join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `student_profiles_${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${filtered.length} student profiles`);
    };

    const getCGPAColor = (cgpa) => {
        if (!cgpa) return 'text-slate-400';
        if (cgpa >= 8.5) return 'text-emerald-600 dark:text-emerald-400 font-bold';
        if (cgpa >= 7.0) return 'text-indigo-600 dark:text-indigo-400 font-bold';
        if (cgpa >= 6.0) return 'text-amber-600 dark:text-amber-400 font-bold';
        return 'text-rose-600 dark:text-rose-400 font-bold';
    };

    return (
        <div className="space-y-6">
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                {/* Header */}
                <div className="bento-header-banner flex flex-wrap gap-4 items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="bg-indigo-50 dark:bg-indigo-950/60 border border-indigo-100 dark:border-indigo-800 p-2 rounded-xl text-indigo-600 dark:text-indigo-400">
                            <FiUser className="text-xl" />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">Student Profiles</h1>
                            <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5">
                                Showing <strong className="text-indigo-600 dark:text-indigo-400">{filtered.length}</strong> of {profiles.length} student profiles (Sorted in Ascending JNTU Roll Order: 01..99, A0..A9, B0..B9).
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setSortAscending(!sortAscending)}
                            className="inline-flex items-center gap-1.5 px-3 py-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-xl transition cursor-pointer border border-slate-200 dark:border-slate-700"
                            title="Toggle Ascending / Descending"
                        >
                            {sortAscending ? <FiArrowUp className="text-indigo-600 dark:text-indigo-400" /> : <FiArrowDown className="text-indigo-600 dark:text-indigo-400" />}
                            <span>{sortAscending ? 'Roll No Ascending' : 'Roll No Descending'}</span>
                        </button>
                        <button
                            onClick={handleExportCSV}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold rounded-xl shadow-xs transition cursor-pointer"
                        >
                            <FiDownload />
                            Export CSV
                        </button>
                    </div>
                </div>

                <div className="p-6 space-y-5">
                    {/* Filters Bar */}
                    <div className="bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 p-4 rounded-2xl space-y-3">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2 text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                <FiFilter size={13} />
                                <span>Live Student Filters</span>
                            </div>
                            {(filters.branch || filters.semester || filters.batch || filters.minCGPA || filters.maxBacklogs || searchQuery) && (
                                <button
                                    onClick={handleResetFilter}
                                    className="px-3 py-1 bg-slate-200 dark:bg-slate-800 hover:bg-slate-300 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 text-xs font-bold rounded-lg transition cursor-pointer flex items-center gap-1"
                                >
                                    <FiRefreshCw size={11} /> Reset Filters
                                </button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            {/* Search */}
                            <div className="col-span-2 relative">
                                <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={13} />
                                <input
                                    type="text"
                                    placeholder="Search name, roll no (e.g. 22N81A0501)..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="w-full pl-8 pr-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                                />
                            </div>
                            {/* Branch */}
                            <select name="branch" value={filters.branch} onChange={handleFilterChange}
                                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">All Branches</option>
                                {filterOptions.branches.map(b => (
                                    <option key={b} value={b}>{b}</option>
                                ))}
                            </select>
                            {/* Semester */}
                            <select name="semester" value={filters.semester} onChange={handleFilterChange}
                                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">All Semesters</option>
                                {filterOptions.semesters.map(s => (
                                    <option key={s} value={s}>Semester {s}</option>
                                ))}
                            </select>
                            {/* Batch */}
                            <select name="batch" value={filters.batch} onChange={handleFilterChange}
                                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500">
                                <option value="">All Batches</option>
                                {filterOptions.batches.map(b => (
                                    <option key={b} value={b}>Batch {b}</option>
                                ))}
                            </select>
                            {/* Min CGPA */}
                            <input type="number" step="0.1" name="minCGPA" value={filters.minCGPA}
                                onChange={handleFilterChange} placeholder="Min CGPA (e.g. 7.5)"
                                className="w-full px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500" />
                        </div>
                        {/* Max Backlogs */}
                        <div className="flex items-center gap-3">
                            <input type="number" name="maxBacklogs" value={filters.maxBacklogs}
                                onChange={handleFilterChange} placeholder="Max Active Backlogs (e.g. 0 for all-clear)"
                                className="w-64 px-3 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl text-xs font-semibold text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500" />
                            <span className="text-[11px] text-slate-400">Filters update automatically as you select</span>
                        </div>
                    </div>

                    {/* Quick Stats */}
                    {profiles.length > 0 && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <div className="p-3.5 bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-xl">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Filtered Students</span>
                                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-0.5">{filtered.length}</p>
                            </div>
                            <div className="p-3.5 bg-emerald-50/60 dark:bg-emerald-950/40 border border-emerald-100 dark:border-emerald-800/60 rounded-xl">
                                <span className="text-[10px] font-bold text-emerald-600 dark:text-emerald-400 uppercase tracking-wider">0 Backlogs</span>
                                <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300 mt-0.5">
                                    {filtered.filter(p => (p.activeBacklogs ?? 0) === 0).length}
                                </p>
                            </div>
                            <div className="p-3.5 bg-indigo-50/60 dark:bg-indigo-950/40 border border-indigo-100 dark:border-indigo-800/60 rounded-xl">
                                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider">Resume Uploaded</span>
                                <p className="text-2xl font-bold text-indigo-700 dark:text-indigo-300 mt-0.5">
                                    {filtered.filter(p => p.resumeLink).length}
                                </p>
                            </div>
                            <div className="p-3.5 bg-amber-50/60 dark:bg-amber-950/40 border border-amber-100 dark:border-amber-800/60 rounded-xl">
                                <span className="text-[10px] font-bold text-amber-600 dark:text-amber-400 uppercase tracking-wider">Avg CGPA</span>
                                <p className="text-2xl font-bold text-amber-700 dark:text-amber-300 mt-0.5">
                                    {(() => {
                                        const valid = filtered.filter(p => p.cgpa);
                                        return valid.length > 0
                                            ? (valid.reduce((sum, p) => sum + (p.cgpa || 0), 0) / valid.length).toFixed(2)
                                            : 'N/A';
                                    })()}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Table */}
                    {loading ? (
                        <div className="text-center py-16 text-slate-400 text-xs font-semibold">
                            <div className="inline-block animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent mb-2"></div>
                            <p>Loading student profiles...</p>
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="text-center py-14 bg-slate-50 dark:bg-slate-950 rounded-2xl border border-slate-200 dark:border-slate-800">
                            <FiUser className="mx-auto text-slate-300 dark:text-slate-600 text-3xl mb-2" />
                            <p className="font-bold text-slate-600 dark:text-slate-400 text-xs">No students match the selected filters</p>
                            <button onClick={handleResetFilter} className="mt-2 text-indigo-600 dark:text-indigo-400 text-xs font-semibold hover:underline cursor-pointer">Reset filters</button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto rounded-xl border border-slate-200 dark:border-slate-800">
                            <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800 text-xs">
                                <thead className="bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400 font-bold uppercase tracking-wider">
                                    <tr>
                                        <th className="py-3 px-3 text-left">#</th>
                                        <th className="py-3 px-4 text-left">Enrollment No</th>
                                        <th className="py-3 px-4 text-left">Student Name</th>
                                        <th className="py-3 px-3 text-left">Branch</th>
                                        <th className="py-3 px-3 text-center">Sem</th>
                                        <th className="py-3 px-3 text-center">Batch</th>
                                        <th className="py-3 px-3 text-center">CGPA</th>
                                        <th className="py-3 px-3 text-center">10th%</th>
                                        <th className="py-3 px-3 text-center">12th%</th>
                                        <th className="py-3 px-3 text-center">Backlogs</th>
                                        <th className="py-3 px-4 text-left">Contact</th>
                                        <th className="py-3 px-4 text-center">Links</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                                    {filtered.map((student, idx) => (
                                        <tr key={student.enrollmentNo || idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition text-slate-800 dark:text-slate-200">
                                            <td className="py-3 px-3 text-slate-400 font-mono">{idx + 1}</td>
                                            <td className="py-3 px-4 font-mono font-bold text-indigo-600 dark:text-indigo-400">
                                                {student.enrollmentNo}
                                            </td>
                                            <td className="py-3 px-4">
                                                <div className="flex items-center gap-2">
                                                    {student.profilePhoto ? (
                                                        <img src={student.profilePhoto} alt="" className="w-6 h-6 rounded-full object-cover shrink-0" />
                                                    ) : (
                                                        <div className="w-6 h-6 rounded-full bg-indigo-100 dark:bg-indigo-950/80 text-indigo-700 dark:text-indigo-300 font-bold flex items-center justify-center text-[10px] shrink-0">
                                                            {student.firstName?.[0] || 'S'}
                                                        </div>
                                                    )}
                                                    <div>
                                                        <p className="font-bold text-slate-900 dark:text-white">{student.fullName}</p>
                                                        <p className="text-[10px] text-slate-400">{student.email}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="py-3 px-3 font-semibold text-slate-700 dark:text-slate-300">{student.branch || '—'}</td>
                                            <td className="py-3 px-3 text-center font-mono">{student.semester || '—'}</td>
                                            <td className="py-3 px-3 text-center font-mono">{student.batch || '—'}</td>
                                            <td className={`py-3 px-3 text-center ${getCGPAColor(student.cgpa)}`}>
                                                {student.cgpa != null ? student.cgpa : '—'}
                                            </td>
                                            <td className="py-3 px-3 text-center font-mono text-slate-600 dark:text-slate-400">
                                                {student.tenthPercentage != null ? `${student.tenthPercentage}%` : '—'}
                                            </td>
                                            <td className="py-3 px-3 text-center font-mono text-slate-600 dark:text-slate-400">
                                                {student.twelfthPercentage != null ? `${student.twelfthPercentage}%` : '—'}
                                            </td>
                                            <td className="py-3 px-3 text-center">
                                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                                                    (student.activeBacklogs || 0) === 0
                                                        ? 'bg-emerald-50 dark:bg-emerald-950/60 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800'
                                                        : 'bg-rose-50 dark:bg-rose-950/60 text-rose-700 dark:text-rose-300 border-rose-200 dark:border-rose-800'
                                                }`}>
                                                    {student.activeBacklogs || 0}
                                                </span>
                                            </td>
                                            <td className="py-3 px-4 text-[11px] text-slate-500 dark:text-slate-400">
                                                <p>{student.phoneNumber || '—'}</p>
                                            </td>
                                            <td className="py-3 px-4 text-center">
                                                <div className="flex items-center justify-center gap-2">
                                                    {student.resumeLink && (
                                                        <a href={student.resumeLink} target="_blank" rel="noopener noreferrer"
                                                            className="p-1.5 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300 rounded-lg border border-indigo-200 dark:border-indigo-800 hover:bg-indigo-100 dark:hover:bg-indigo-900 transition"
                                                            title="View Resume">
                                                            <FiFileText size={12} />
                                                        </a>
                                                    )}
                                                    {student.linkedinLink && (
                                                        <a href={student.linkedinLink} target="_blank" rel="noopener noreferrer"
                                                            className="p-1.5 bg-blue-50 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 rounded-lg border border-blue-200 dark:border-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900 transition"
                                                            title="View Profile Link">
                                                            <FiExternalLink size={12} />
                                                        </a>
                                                    )}
                                                    {!student.resumeLink && !student.linkedinLink && (
                                                        <span className="text-slate-300 dark:text-slate-600 text-[10px]">—</span>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default StudentProfiles;
